import React, { useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { NavigationClient } from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import CryptoJS from "crypto-js";
import { CustomToaster } from "utils/reusableToast";

import OrgsList from "components/orgList/orgList";
import RequireSpecificEmails from "components/RequireSpecificEmails/requireSpecificEmails";

import AdminLayout from "./components/AdminLayout/AdminLayout";
import Layout from "./components/Layout/Layout";
import NotFound from "./components/NotFound/NotFound";
import RequireAuth from "./components/RequireAuth/RequireAuth";
import RequireRoles from "./components/RequireRoles/RequireRoles";
import CopilotDashboard from "./pages/admin/CopilotDashboard";
import InactiveUsers from "./pages/admin/InactiveUsers";
import PendingApprovalsTabs from "./pages/admin/components/PendingApprovalsTabs/PendingApprovalsTabs";
import Home from "./pages/home/home";
import InactiveLogout from "./pages/home/InactiveLogout/InactiveLogout";
import HomeDashboard from "./pages/homeDashboard/HomeDashboard";
import OrgHistory from "./pages/OrgHistory/OrgHistory";
import OrgSettings from "./pages/orgSettings/OrgSettings";
import OpsOrgDetail from "./pages/opsDashboard/OpsOrgDetail";
import OpsOrgList from "./pages/opsDashboard/OpsOrgList";
import EcrOpsLayout from "./pages/ecrOps/EcrOpsLayout";
import EcrOpsHome from "./pages/ecrOps/EcrOpsHome";
import EcrOpsOrgManagement from "./pages/ecrOps/EcrOpsOrgManagement";
import EcrOpsUnifiedSearch from "./pages/ecrOps/EcrOpsUnifiedSearch";
import ActiveDirectoryUserLookup from "./pages/ecrOps/ActiveDirectoryUserLookup";
import EcrOpsNewOrgs from "./pages/ecrOps/EcrOpsNewOrgs";
import EcrOpsCompleteOrg from "./pages/ecrOps/EcrOpsCompleteOrg";
import EcrOpsHealthStatus from "./pages/ecrOps/EcrOpsHealthStatus";
import EcrOpsPortalConfig from "./pages/ecrOps/EcrOpsPortalConfig";
import EcrOpsCopilotEnterprise from "./pages/ecrOps/EcrOpsCopilotEnterprise";
// import DocsHome from "./pages/docs/DocsHome";
// import BlogPost from "./pages/docs/BlogPost";
// import HowToPage from "./pages/docs/HowToPage";
// import WhatIsECR from "./pages/docs/WhatIsECR";
// import PricingPage from "./pages/docs/PricingPage";
import { setAuthState } from "./state/slices/auth/authSlice";

import "./App.css";

class CustomNavigationClient extends NavigationClient {
  constructor(navigate) {
    super();
    this.navigate = navigate; // Passed in from useNavigate hook provided by react-router-dom;
  }
  // This function will be called anytime msal needs to navigate from one page in your application to another
  async navigateInternal(url, options) {
    // url will be absolute, you will need to parse out the relative path to provide to the history API
    const relativePath = url.replace(window.location.origin, "/");
    if (options.noHistory) {
      this.navigate(relativePath, { replace: true });
    } else {
      this.navigate(relativePath);
    }

    return false;
  }
}

function AppRoutes({ msalInstance }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Set the custom navigation client on the msal instance
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  // Get encrypted auth token from localstorage, decrypt, and store in auth slice to rehydrate state on refresh
  // This is a temporary solution until we implement refresh tokens
  const authToken = useSelector((state) => state.auth.authToken);
  if (!authToken && localStorage.getItem("authStateEncrypted")) {
    const decryptedAuthToken = CryptoJS.AES.decrypt(
      localStorage.getItem("authStateEncrypted"),
      "someKey"
    ).toString(CryptoJS.enc.Utf8);

    dispatch(setAuthState(JSON.parse(decryptedAuthToken)));
  }

  // scroll to top of page after a page transition.
  useLayoutEffect(() => {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />


      {/* Protected Routes */}
      <Route path="/*" element={<RequireAuth />}>

        <Route element={<Layout />}>
          <Route path="dashboard" element={<HomeDashboard />} />
          <Route path="global-orgs" element={<OrgsList />} />
          {/* <Route path="docs" element={<DocsHome />} /> */}
          {/* <Route path="docs/blog/:slug" element={<BlogPost />} />
          <Route path="docs/how-to-users" element={<HowToPage audience="users" />} />
          <Route path="docs/how-to-owners" element={<HowToPage audience="owners" />} />
          <Route path="docs/what-is-ecr" element={<WhatIsECR />} />
          <Route path="docs/pricing" element={<PricingPage />} /> */}
          {/* ECR Ops Portal — all ops tools live under /ecr-ops */}
          <Route
            path="ecr-ops"
            element={<RequireSpecificEmails />}
          >
            <Route element={<EcrOpsLayout />}>
              <Route index element={<EcrOpsHome />} />
              <Route path="org-management" element={<EcrOpsOrgManagement />} />
              {/* Unified search — single page, all sources */}
              <Route path="search" element={<EcrOpsUnifiedSearch />} />
              {/* Legacy route - redirect or keep for backwards compatibility */}
              <Route path="ad-user-lookup" element={<ActiveDirectoryUserLookup />} />
              <Route path="new-orgs" element={<EcrOpsNewOrgs />} />
              <Route path="complete-org/:orgId" element={<EcrOpsCompleteOrg />} />
              <Route path="health" element={<EcrOpsHealthStatus />} />
              <Route path="copilot-enterprise" element={<EcrOpsCopilotEnterprise />} />
              <Route path="portal-config" element={<EcrOpsPortalConfig />} />
              <Route path="stuck-users" element={<OpsOrgList />} />
              <Route path="stuck-users/:orgId" element={<OpsOrgDetail />} />
            </Route>
          </Route>
          {/* Role-Based Protected Routes */}
          <Route
            path="admin/:orgId"
            element={<RequireRoles allowedRoles={["Owner", "Approver"]} />}
          >
            <Route element={<AdminLayout />}>
              {/* Owner and Approver Routes */}
              <Route path="dashboard" element={<PendingApprovalsTabs />} />
              <Route path="history" element={<OrgHistory />} />
              <Route path="inactive-users" element={<InactiveUsers />} />
              <Route path="copilot-dashboard" element={<CopilotDashboard />} />

              {/* Owner only Routes */}
              <Route
                path=""
                element={<RequireRoles allowedRoles={["Owner"]} />}
              >
                <Route path="settings" element={<OrgSettings />} />
              </Route>
            </Route>
          </Route>

          {/* Catch all */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App({ msalInstance }) {
  // It's important that the Router component is above the Example component because you'll need to use the useHistory hook before rendering MsalProvider

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <MsalProvider instance={msalInstance}>
        <CustomToaster />
        <InactiveLogout>
          <AppRoutes msalInstance={msalInstance} />
        </InactiveLogout>
      </MsalProvider>
    </Router>
  );
}

export default App;
